// ===== STEP 5: CREATE CARDRENDERER.JS =====
// File: ContentBlocks/renderers/CardRenderer.js

import React, { useEffect, useState } from 'react';
import { RichTextEditor } from '../../components/LazyEditors';
import SafeHtml from '../../components/SafeHtml';

// Helper function to determine if text should be dark or light based on background color
const getContrastingTextColor = (hexColor) => {
    if (!hexColor || typeof hexColor !== 'string') return '#374151';
    const sanitized = hexColor.replace('#', '');
    if (![3, 6].includes(sanitized.length)) return '#374151';
    const fullHex = sanitized.length === 3
        ? sanitized.split('').map((c) => c + c).join('')
        : sanitized;
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#374151';
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 150 ? '#1f2937' : '#f9fafb';
};

const CardRenderer = ({ block, isEditMode, onBlockUpdate }) => {
    const [activeCardIndex, setActiveCardIndex] = useState(null);

    useEffect(() => {
        if (!isEditMode) {
            setActiveCardIndex(null);
        }
    }, [isEditMode]);
    const layoutClass = {
        '2x1': 'grid-cols-1 md:grid-cols-2',
        '2x2': 'grid-cols-1 md:grid-cols-2',
        '3x1': 'grid-cols-1 md:grid-cols-3',
        '1x3': 'grid-cols-1'
    }[block.layout] || 'grid-cols-1 md:grid-cols-2';

    const cardStyleConfig = {
        info: { bg: '#f8fafc', border: '#94a3b8', accent: '#334155' },
        exercise: { bg: '#ecfdf5', border: '#34d399', accent: '#065f46' },
        warning: { bg: '#fef3c7', border: '#fbbf24', accent: '#92400e' },
        success: { bg: '#f0fdf4', border: '#4ade80', accent: '#166534' }
    }[block.style] || { bg: '#f8fafc', border: '#94a3b8', accent: '#334155' };

    // Calculate grid template columns based on card widths
    const getGridTemplateColumns = () => {
        if (!block.items || block.items.length === 0) return undefined;

        // Check if any card has a custom width percentage
        const hasCustomWidths = block.items.some(item => item.widthPercent && item.widthPercent !== 100);

        if (!hasCustomWidths) return undefined; // Use default Tailwind grid classes

        // Convert percentage widths to fractional units
        // Base unit is 1fr = 100%, so 200% = 2fr, 50% = 0.5fr, etc.
        const columns = block.items.map(item => {
            const widthPercent = item.widthPercent || 100;
            const frValue = widthPercent / 100;
            return `${frValue}fr`;
        }).join(' ');

        return columns;
    };

    const gridTemplateColumns = getGridTemplateColumns();
    const gridStyle = gridTemplateColumns ? {
        display: 'grid',
        gridTemplateColumns: gridTemplateColumns,
        gap: '1rem'
    } : {};

    const handleCardUpdate = (index, updates) => {
        const nextItems = (block.items || []).map((item, itemIndex) => (
            itemIndex === index ? { ...item, ...updates } : item
        ));
        onBlockUpdate?.({ ...block, items: nextItems });
    };

    const handleActivateCard = (event, index) => {
        if (!isEditMode) return;
        if (event.target.closest('button, a, input, textarea, select')) return;
        setActiveCardIndex(index);
    };

    return (
        <div className="my-6">
            <div
                className={gridTemplateColumns ? '' : `grid ${layoutClass} gap-4`}
                style={gridStyle}
            >
                {(block.items || []).map((item, index) => {
                    const isActive = isEditMode && activeCardIndex === index;
                    // Use custom colors only if useCustomColors is true
                    const useCustom = item.useCustomColors && item.backgroundColor;
                    const backgroundColor = useCustom ? item.backgroundColor : cardStyleConfig.bg;
                    const borderColor = useCustom && item.borderColor ? item.borderColor : cardStyleConfig.border;
                    const titleColor = useCustom
                        ? getContrastingTextColor(backgroundColor)
                        : cardStyleConfig.accent;
                    const contentColor = useCustom
                        ? getContrastingTextColor(backgroundColor)
                        : '#374151';

                    return (
                        <div
                            key={index}
                            className="p-6 rounded-xl border-l-4 shadow-sm hover:shadow-md transition-shadow"
                            style={{
                                backgroundColor,
                                borderLeftColor: borderColor,
                                color: contentColor
                            }}
                            onClick={(event) => handleActivateCard(event, index)}
                        >
                            {isActive ? (
                                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                                    <input
                                        type="text"
                                        value={item.title || ''}
                                        onChange={(event) => handleCardUpdate(index, { title: event.target.value })}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-slate-800"
                                        placeholder="Card title"
                                    />
                                    <RichTextEditor
                                        content={item.content || ''}
                                        onChange={(content) => handleCardUpdate(index, { content })}
                                        isPreviewMode={false}
                                    />
                                </div>
                            ) : (
                                <>
                                    <h4
                                        className="font-semibold mb-3"
                                        style={{ color: titleColor }}
                                    >
                                        {item.title}
                                    </h4>
                                    <SafeHtml
                                        className="card-content"
                                        style={{ color: contentColor }}
                                        html={item.content}
                                    />
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CardRenderer;
